import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-preserve-media-order') && this.dataset.initialMediaId) {
      customElements.whenDefined('slideshow-component').then(() => {
        if (!this.isConnected || this.#selectionRequest !== 0) return;
        this.#initialFrame = requestAnimationFrame(() => {
          this.#initialFrame = requestAnimationFrame(() => {
            if (this.isConnected && this.#selectionRequest === 0) {
              this.#selectMedia(this.dataset.initialMediaId, false);
            }
          });
        });
      });
    }
  }

  #controller = new AbortController();
  #selectionRequest = 0;
  #initialFrame = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#selectionRequest++;
    cancelAnimationFrame(this.#initialFrame);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const request = ++this.#selectionRequest;

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || request !== this.#selectionRequest || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (
          this.hasAttribute('data-preserve-media-order') &&
          this.dataset.productId === newMediaGallery.dataset.productId
        ) {
          const mediaId = detail.resource?.featured_media?.id;
          if (mediaId) this.#selectMedia(String(mediaId), true);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #selectMedia(mediaId, reveal) {
    if (!mediaId) return;

    const slides = Array.from(this.slideshow?.querySelectorAll('slideshow-slide') ?? []);
    const index = slides.findIndex((slide) => slide.querySelector('[data-media-id]')?.dataset.mediaId === mediaId);
    if (index < 0) return;

    this.dataset.selectedMediaId = mediaId;
    this.slideshow?.select(index, undefined, { animate: false });

    if (!reveal) return;

    const grid = this.querySelector('.media-gallery__grid');
    const target = grid && getComputedStyle(grid).display !== 'none'
      ? Array.from(grid.children).find((item) => item.querySelector('[data-media-id]')?.dataset.mediaId === mediaId)
      : this.slideshow;

    target?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
